import { Journey } from '@flysas-tech/book-value-stream-models';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { FlightDurationProps, FlightTimeProps } from 'models';
import { BoundFlight, FlightSegment } from 'shared';

export function getDurationInSecFromString(connectionDuration: string) {
  return connectionDuration.split(' ').reduce((seconds: number, _duration: string) => {
    if (_duration.includes('h')) {
      seconds += +_duration.replace(/h|m/g, '') * 60 * 60;
    }

    if (_duration.includes('m')) {
      seconds += +_duration.replace(/h|m/g, '') * 60;
    }

    return seconds;
  }, 0);
}

export function getLongestFlightDuration(flights: { connectionDuration: string }[]) {
  return Math.max(...flights.map(flight => getDurationInSecFromString(flight.connectionDuration)));
}

export function getDurationInSec(timeA: string, timeB: string): number {
  return DateTime.fromISO(timeA ? timeA : '').diff(DateTime.fromISO(timeB ? timeB : ''), ['seconds']).seconds;
}

const originalTimezoneOffset = { setZone: true };
const formattedTime = (dateTime: DateTime) => dateTime.toFormat('HH:mm');
const formattedDate = (date: DateTime) => date.toFormat('ccc dd LLL yyyy');
const getFlightDateNoYear = (date: DateTime) => date.toFormat('ccc dd LLL');

export const getFlightTimeDuration = (arrivalTimeGMT: string, departureTimeGMT: string) =>
  DateTime.fromISO(arrivalTimeGMT).diff(DateTime.fromISO(departureTimeGMT), ['hours', 'minutes']);

export const getLayoverTimesAndDuration = (
  currentSegmentArrivalTimeLocal: string,
  nextSegmentDepartureTimeLocal: string
) => {
  const duration = DateTime.fromISO(nextSegmentDepartureTimeLocal).diff(
    DateTime.fromISO(currentSegmentArrivalTimeLocal),
    ['hours', 'minutes']
  );
  const { hours, minutes } = duration;
  const departureTime = formattedTime(DateTime.fromISO(nextSegmentDepartureTimeLocal));
  const arrivalTime = formattedTime(DateTime.fromISO(currentSegmentArrivalTimeLocal));
  return { hours, minutes, departureTime, arrivalTime };
};

export const getFlightLayoverDuration = (arrivalTimeGMT: string, departureTimeGMT: string): Duration => {
  return DateTime.fromISO(departureTimeGMT).diff(DateTime.fromISO(arrivalTimeGMT), ['hours', 'minutes']);
};

export const getFlightStopText = (numStops: number): string => {
  switch (numStops) {
    case 0:
      return t('common:direct');
    case 1:
      return t('common:stops.one');
    default:
      return t('common:stops.other', { count: numStops });
  }
};

export const getFlightDate = (departureTimeLocal: string) =>
  formattedDate(DateTime.fromISO(departureTimeLocal, originalTimezoneOffset));

export const getFlightTimeDateAndDuration = ({
  departureTimeLocal,
  arrivalTimeLocal,
  departureTimeGMT,
  arrivalTimeGMT,
  flightSegments,
}: FlightTimeProps) => {
  const flightDepartureDate = getFlightDate(departureTimeLocal);
  const departureTime = formattedTime(DateTime.fromISO(departureTimeLocal, originalTimezoneOffset));
  const arrivalTime = formattedTime(DateTime.fromISO(arrivalTimeLocal, originalTimezoneOffset));
  const duration = getFlightTimeDuration(arrivalTimeGMT, departureTimeGMT);
  const { hours, minutes } = duration;
  const flightStops = getFlightStopText(flightSegments - 1);

  return {
    flightDepartureDate,
    departureTime,
    arrivalTime,
    hours,
    minutes,
    flightStops,
  };
};

export const getFlightDateAndTime = ({ departureTimeLocal, arrivalTimeLocal }: FlightDurationProps) => {
  const flightDepartureDate = getFlightDateNoYear(DateTime.fromISO(departureTimeLocal, originalTimezoneOffset));
  const flightArrivalDate = getFlightDateNoYear(DateTime.fromISO(arrivalTimeLocal, originalTimezoneOffset));
  const departureTime = formattedTime(DateTime.fromISO(departureTimeLocal, originalTimezoneOffset));
  const arrivalTime = formattedTime(DateTime.fromISO(arrivalTimeLocal, originalTimezoneOffset));

  return { flightDepartureDate, flightArrivalDate, departureTime, arrivalTime };
};

export function buildOperatedByString(flight: BoundFlight) {
  return flight.segments
    .map(segment => {
      const carrierName = segment.operatingCarrier.name || segment.marketingCarrier.name;
      const airports = `(${segment.departureAirport.code}-${segment.arrivalAirport.code})`;
      return `${carrierName} ${airports}`;
    })
    .join(' & ');
}

export function convertFlightSegmentsToStops(flight: BoundFlight) {
  const segments = flight.segments;

  const stops: string[] = [segments[0].departureAirport.code];

  segments.forEach(segment => {
    stops.push(segment.arrivalAirport.code);
  });

  return stops;
}

export function mapSegementDetails(segments: Journey['segments'] | FlightSegment[]) {
  return segments.map(segment => {
    const departureDateTime = DateTime.fromISO(segment.departure.dateTime, { setZone: true });
    const arrivelDateTime = DateTime.fromISO(segment.arrival.dateTime, { setZone: true });

    if (!departureDateTime.isValid || !arrivelDateTime.isValid) {
      throw new Error('Invalid flight dates');
    }

    return {
      departureDateTime,
      arrivelDateTime,

      departureDetails: {
        airportName: segment.departure.airport.name,
        airportCode: segment.departure.airport.code,
        city: segment.departure.city.name,
        terminal: segment.departure.terminal,
      },

      arrivalDetails: {
        airportName: segment.arrival.airport.name,
        airportCode: segment.arrival.airport.code,
        city: segment.arrival.city.name,
        terminal: segment.arrival.terminal,
      },
      flightDetails: {
        flight: `${segment.marketingCarrier.code ?? segment.operatingCarrier.code}${segment.flightNumber}`,
        aircraft: segment.airCraft.name,
        operatedBy: segment.operatingCarrier.name,
      },
    };
  });
}

export function getDurationString(from: DateTime, to: DateTime, options: { long: boolean } = { long: false }) {
  const duration = to.diff(from, ['hours', 'minutes']);
  const hasHours = duration.hours > 0;
  const hasMinutes = duration.minutes > 0;

  if (options.long) {
    return t('common:durationLong', {
      defaultValue: '{{hours}} hours {{minutes}} minutes',
      hours: duration.hours,
      minutes: duration.minutes,
    });
  }

  let durationString = hasHours ? `${duration.hours}h` : '';

  if (hasHours && hasMinutes) {
    durationString += ', ';
  }

  durationString += hasMinutes ? `${duration.minutes}m` : '';

  return durationString;
}
